import { ENROLLMENT_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { REGEX_WITH_ACCENTEDCHARS } from '@utils/validators/regex';
import { z } from 'zod';

// Define the Zod schema for enrollment
export const enrollmentSchema = (enrollment, userPermissions) =>
    z
        .object({
            enrollmentBenefitEffectiveStartDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.benefitStartDate',
            }),
            enrollmentBenefitEffectiveEndDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriberEndDate',
            }),

            enrolleeDataDtoList: z.array(
                z.object({
                    firstName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.firstName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.firstName',
                        }),
                    middleName: z
                        .string()
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.middleName',
                        })
                        .nullable()
                        .optional(),
                    lastName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.lastName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.lastName',
                        }),
                    genderLookupLabel: z.string(),
                    taxIdentificationNumber: z.string().nullable(),
                    birthDate: z.string().optional(),
                    enrolleeEffectiveStartDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter',
                    }),
                    enrolleeEffectiveEndDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnterEndDate',
                    }),
                }),
            ),
            enrollmentPremiumDtoList: z.array(
                z
                    .object({
                        month: z.number(),
                        year: z.number(),
                        slcspPremiumAmount: z.number().optional(),
                        isFinancial: z.boolean().optional(),
                        isAptcEligible: z.boolean().optional(),
                        isStateSubsidyEligible: z.boolean().optional(),
                        aptcAmount: z.number().nullable().optional(),
                        grossPremiumAmount: z.number().optional(),
                        maxAptc: z.number().nullable().optional(),
                        maxStateSubsidy: z.number().nullable().optional(),
                        minimumPremium: z.number().optional(),
                        netPremiumAmount: z.union([z.number(), z.nan()]).optional(),
                        stateSubsidyAmount: z.number().nullable().optional(),
                        stateSubsidy2Amount: z.number().nullable().optional(),
                        maxStateSubsidy2: z.number().nullable().optional(),
                        medicaidTransitionAmount: z.number().nullable().optional(),
                        isMedicaidTransitionEligible: z.boolean().optional(),
                    })
                    .superRefine((data, customError) =>
                        aptcRequiredValidation(data, customError, enrollment, userPermissions),
                    ),
            ),
        })
        .superRefine((data, customError) => DateValidations(data, customError, enrollment));

const aptcRequiredValidation = (data, customError, enrollment, userPermissions) => {
    const ehbPercent = enrollment.ehbPercentage;
    const premium = { ...data };
    if (data.isFinancial) {
        if (data.maxAptc === undefined || data.maxAptc === null) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['maxAptc'],
                message: 'error.member.enrollment_list.premiumhistory.validNumber',
            });
        }
        if (data.aptcAmount === undefined || data.aptcAmount === null) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['aptcAmount'],
                message: 'error.member.enrollment_list.premiumhistory.validNumber',
            });
        }

        if (ehbPercent !== null && ehbPercent !== 0) {
            if (Number((premium.grossPremiumAmount * ehbPercent).toFixed(2)) < Number(premium.aptcAmount.toFixed(2))) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['aptcAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual2',
                });
            }
        } else {
            if (premium.grossPremiumAmount < premium.aptcAmount) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['aptcAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual2',
                });
            }
        }
        if (premium.maxAptc !== undefined && premium.aptcAmount > premium.maxAptc) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['aptcAmount'],
                message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual',
            });
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['maxAptc'],
                message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
            });
        }

        if (
            enrollment.isStateSubsidyEnabled &&
            userPermissions?.memberEnrlmntAptcEditable &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELLED
        ) {
            if (premium.stateSubsidyAmount === undefined || premium.stateSubsidyAmount === null) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['stateSubsidyAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.validNumber',
                });
            }
            if (premium.maxStateSubsidy === undefined || premium.maxStateSubsidy === null) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['maxStateSubsidy'],
                    message: 'error.member.enrollment_list.premiumhistory.validNumber',
                });
            }

            if (
                premium.maxStateSubsidy !== undefined &&
                premium.maxStateSubsidy !== null &&
                premium.stateSubsidyAmount > premium.maxStateSubsidy
            ) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['stateSubsidyAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.stateSubsidy.lessOrEqual',
                });
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['maxStateSubsidy'],
                    message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
                });
            }

            if (enrollment.isStateSubsidy2Enabled) {
                if (premium.stateSubsidy2Amount === undefined || premium.stateSubsidy2Amount === null) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidy2Amount'],
                        message: 'error.member.enrollment_list.premiumhistory.validNumber',
                    });
                }

                if (
                    premium.stateSubsidy2Amount === undefined &&
                    premium.stateSubsidy2Amount === null &&
                    premium.maxStateSubsidy2 !== null &&
                    premium.maxStateSubsidy2 !== undefined &&
                    premium.stateSubsidy2Amount > premium?.maxStateSubsidy2
                ) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidy2Amount'],
                        message: 'error.member.enrollment_list.premiumhistory.stateSubsidy.lessOrEqual',
                    });
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['maxStateSubsidy2'],
                        message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
                    });
                }
            }

            if (ehbPercent !== null && ehbPercent !== 0) {
                if (
                    Number((premium.grossPremiumAmount * ehbPercent).toFixed(2)) <
                    Number((premium.stateSubsidyAmount + (premium.stateSubsidy2Amount || 0)).toFixed(2))
                ) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidyAmount'],
                        message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                    });
                    if (premium.isStateSubsidy2Enabled) {
                        customError.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['stateSubsidy2Amount'],
                            message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                        });
                    }
                }
            } else {
                if (premium.grossPremiumAmount < premium.stateSubsidyAmount + (premium.stateSubsidy2Amount || 0)) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidyAmount'],
                        message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                    });

                    if (premium.isStateSubsidy2Enabled) {
                        customError.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['stateSubsidy2Amount'],
                            message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                        });
                    }
                }
            }
        }
    }
    return z.NEVER;
};

const DateValidations = (data, customError, enrollment) => {
    const birthDate = new Date(data.enrolleeDataDtoList?.[0]?.birthDate || '');
    const enrollmentBenefitEffectiveStartDateUI = new Date(data.enrollmentBenefitEffectiveStartDateUI);
    if (birthDate > enrollmentBenefitEffectiveStartDateUI) {
        customError.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enrollmentBenefitEffectiveStartDateUI'],
            message: 'error.member.enrollment_list.premiumhistory.enrollee.early.benefitStartDate',
        });
    }

    if (data.enrollmentBenefitEffectiveEndDateUI === data.enrollmentBenefitEffectiveStartDateUI) {
        if (enrollment.enrollmentBenefitEffectiveStartDateUI !== data.enrollmentBenefitEffectiveStartDateUI) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveStartDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.cancellation.benefitStartDate',
            });
        }
        if (enrollment.enrollmentBenefitEffectiveEndDateUI !== data.enrollmentBenefitEffectiveEndDateUI) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveEndDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriber',
            });
        }
    }
};
